import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';

import SideDialog from '../../../../common/SideDialog';
import {
  Container,
  TopRow,
  AddUsersButton,
  Loader,
  Content,
} from './AddUserDialog.styled';
import UserSearch from './UserSearch/UserSearch';
import UserSearchResultsList from './UserSearchResultsList/UserSearchResultsList';
import NotEducatorsWarning from './NotEducatorsWarning/NotEducatorsWarning';
import { User } from './types';

const AddUserDialog = ({
  open,
  onClose,
  searchUsers,
  participants,
  isAddingParticipantToSpace,
  addUsersAsEducatorToSpace,
  hasAddParticipantError,
}) => {
  const [usersResult, setUsersResult] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFetching, setFetching] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const inputRef = useRef();

  const onSearch = async (value, selectedUsers, participants) => {
    setFetching(true);
    setUsersResult([]);
    const users = await searchUsers(value, selectedUsers, participants);
    setUsersResult(users);
    setFetching(false);
    inputRef.current.focus();
  };
  const debouncedSearch = useCallback(debounce(onSearch, 200), []);

  const onSearchQueryChanged = (value) => {
    setSearchQuery(value);
    if (!value.trim().length) {
      return;
    }

    setFetching(true);
    debouncedSearch(value, selectedUsers, participants);
  };

  const onSelectUser = (user) => () => {
    setSelectedUsers([...selectedUsers, user]);
    setSearchQuery('');
    inputRef.current.focus();
  };

  const onCloseDialog = (event) => {
    setUsersResult([]);
    setSearchQuery('');
    setFetching(false);
    setSelectedUsers([]);
    onClose(event);
  };

  useEffect(() => {
    if (
      isAddingParticipantToSpace === false &&
      hasAddParticipantError === false
    ) {
      onCloseDialog();
    }
  }, [hasAddParticipantError, isAddingParticipantToSpace]);
  const addSelectedUsersToSpace = () =>
    addUsersAsEducatorToSpace(selectedUsers.map(({ id }) => id));

  const notEducatorUsers = selectedUsers.filter(
    (user) => user.educatorInSpaces.length === 0
  );
  return (
    <SideDialog
      title="Add educator"
      open={open}
      onClose={onCloseDialog}
      width="800px"
      testAttribute="add-educator-dialog"
    >
      <Container>
        <TopRow data-testid="self-serve-search-row">
          <UserSearch
            title="Search educators by name or email"
            onSearchQueryChanged={onSearchQueryChanged}
            searchQuery={searchQuery}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
            onTextReset={() => setUsersResult([])}
            ref={inputRef}
          />
          <AddUsersButton
            onClick={addSelectedUsersToSpace}
            disabled={!selectedUsers.length}
            className={isAddingParticipantToSpace ? 'addingParticipant' : null}
          >
            {isAddingParticipantToSpace ? <Loader /> : 'Add selected'}
          </AddUsersButton>
        </TopRow>
        {notEducatorUsers.length ? (
          <NotEducatorsWarning notEducatorUsers={notEducatorUsers} />
        ) : null}
      </Container>
      <Content>
        <UserSearchResultsList
          searchQuery={searchQuery}
          onSelectUser={onSelectUser}
          selectedUsers={selectedUsers}
          users={usersResult}
          isFetching={isFetching}
        />
      </Content>
    </SideDialog>
  );
};

AddUserDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  searchUsers: PropTypes.func.isRequired,
  participants: PropTypes.arrayOf(User),
  isAddingParticipantToSpace: PropTypes.bool.isRequired,
  hasAddParticipantError: PropTypes.bool.isRequired,
  addUsersAsEducatorToSpace: PropTypes.func.isRequired,
};

AddUserDialog.defaultProps = {
  participants: [],
};

export default AddUserDialog;
